import { translateRequestError } from "@/utils/errors";

import { NamedList, reportRefusedLists } from "./listRefusalMessage";

import type { ArchiveResponse, DownloadFile, List } from "../schema/list";

type Unwrappable<T> = { unwrap: () => Promise<T> };
type ArchiveMutation = (body: { ids: number[]; archived: boolean }) => Unwrappable<ArchiveResponse>;
type DeleteListsMutation = (ids: number[]) => Unwrappable<ArchiveResponse>;

/**
 * Runs a bulk archive/delete and answers whether the API refused any of the lists (PREC-4537),
 * showing the refused ids through `notify`. Keeping this here keeps `ListTable` under its length
 * ratchet while the handlers still report the server's answer instead of closing as if it worked.
 */
export const archiveListsAndReport = async (
	archive: ArchiveMutation,
	ids: number[],
	archived: boolean,
	notify: (message: string) => void,
	lists: readonly NamedList[] = []
): Promise<boolean> => reportRefusedLists(await archive({ ids, archived }).unwrap(), notify, lists);

export const deleteListsAndReport = async (
	remove: DeleteListsMutation,
	ids: number[],
	notify: (message: string) => void,
	lists: readonly NamedList[] = []
): Promise<boolean> => reportRefusedLists(await remove(ids).unwrap(), notify, lists);

export interface ListLifecycleActionDeps {
	selectedFiles: DownloadFile[];
	selectedList?: List | null;
	archiveList: ArchiveMutation;
	deleteLists: DeleteListsMutation;
	deleteList: (id: number) => Unwrappable<unknown>;
	notify: (message: string) => void;
	clearBulkSelection: () => void;
	setPage: (page: number) => void;
	setShowDeleteListsModal: (open: boolean) => void;
	setShowDeleteModal: (open: boolean) => void;
	setShowArchiveModal: (open: boolean) => void;
}

/**
 * The Lists overview's archive/delete handlers (PREC-4537). A refused answer keeps the selection
 * and the dialog open and shows the server's reason; only a clean answer closes them.
 */
export const listLifecycleActions = (deps: ListLifecycleActionDeps) => {
	const selectedIds = () =>
		deps.selectedFiles.filter((file) => file.specialType === null).map((file) => file.id);

	const handleArchiveSelected = async () => {
		if (
			await archiveListsAndReport(
				deps.archiveList,
				selectedIds(),
				true,
				deps.notify,
				deps.selectedFiles
			)
		) {
			return;
		}

		deps.clearBulkSelection();
	};

	const handleDeleteSelected = async () => {
		deps.setPage(1);

		if (
			await deleteListsAndReport(deps.deleteLists, selectedIds(), deps.notify, deps.selectedFiles)
		) {
			return;
		}

		deps.setShowDeleteListsModal(false);
		deps.clearBulkSelection();
	};

	const handleDeleteList = async () => {
		try {
			await deps.deleteList(deps.selectedList?.id ?? 0).unwrap();
			deps.setShowDeleteModal(false);
		} catch (error) {
			deps.notify(translateRequestError(error));
		}
	};

	const handleArchiveList = async () => {
		try {
			const refused = await archiveListsAndReport(
				deps.archiveList,
				[deps.selectedList?.id ?? 0],
				!deps.selectedList?.archived,
				deps.notify,
				deps.selectedList ? [deps.selectedList] : []
			);

			if (!refused) {
				deps.setShowArchiveModal(false);
			}
		} catch (error) {
			deps.notify(translateRequestError(error));
		}
	};

	return { handleArchiveSelected, handleDeleteSelected, handleDeleteList, handleArchiveList };
};
